import { apiClient } from "@/api/softphone_api";
import { apiClientJoin } from "@/api/join_api";
import {
  fetchAlerts,
  traducirAlertaSocket,
  markAlertSeen,
  markAllSeen,
  markItemsSeen,
  quienMira,
  simularAlerta,
} from "@/modules/fxCrm/helpers/assignmentAlertsApi";
import {
  avisoEntrada,
  avisoEnvio,
  avisoLlegada,
} from "@/modules/fxCrm/helpers/assignmentAlertTexts";
import { fetchPendingCount, traducirAlerta, traducirSolicitud } from "@/modules/fxCrm/helpers/approvalsApi";
import { avisoResolucion, avisoNuevaSolicitud } from "@/modules/fxCrm/helpers/approvalTexts";
import { reassignScope } from "@/modules/fxCrm/helpers/reassignTargets";
import {
  fetchToDo,
  fetchNews,
  fetchFollowUp,
  completeTask,
  esTareaDesaparecida,
  grupoVacio,
  seguimientoVacio,
} from "@/modules/fxCrm/helpers/bellApi";

/**
 * Contador de «Aprobaciones» en el menú lateral (task_2 CA-01, CA-04): el número
 * de solicitudes pendientes del alcance del rol. Solo para admin y coordinador;
 * para los demás queda en null y el menú no pinta nada. Si falla, se conserva
 * el último valor: un contador que parpadea a 0 confunde más que uno atrasado.
 */
/**
 * Al gestor le resolvieron una solicitud (task_3 CA-01). La llama el socket
 * `crm_approval_alert` con `{ alert, unseen }` (FC-690). Avisa con el mismo
 * aviso emergente de las asignaciones, con «Ver la interacción», y avisa al
 * detalle abierto de esa interacción para que recargue su banda de cierre.
 * `unseen` no se usa: la campana no tiene sección de aprobaciones (fuera de
 * alcance; `CrmApprovalAlert/` queda para cuando se decida).
 */
export const receiveApprovalResolution = ({ commit, state, rootState }, payload) => {
  const moneda = rootState.auth_store?.companyInfo?.currency || null;
  const solicitud = traducirAlerta(payload?.alert ?? payload, moneda);
  if (!solicitud?.managementId || !["aprobada", "rechazada"].includes(solicitud.estado)) return;
  commit("setState", {
    key: "approvalResolution",
    value: { seq: state.approvalResolution.seq + 1, managementId: solicitud.managementId },
  });
  commit("pushAssignmentToast", avisoResolucion(solicitud));
};

/**
 * A quien resuelve (admin o coordinador) le cambió la cola de aprobaciones. La
 * llama el socket `crm_approval_pending` con `{ action, approval, pending_count }`
 * (FC-686 CA-05: al registrarse una solicitud, `action: "created"`).
 * - El contador del menú toma `pending_count` tal cual; si no viene, se pide.
 * - Con «created», aviso emergente «Nueva solicitud de aprobación».
 * - Sube `approvalQueueChange.seq` para que `FxApprovals`, si está abierta,
 *   actualice sus listas.
 * La solicitud no trae moneda (`currency: null`): se usa la de la empresa.
 */
export const receiveApprovalPending = ({ commit, dispatch, state, rootState }, payload) => {
  if (!reassignScope(rootState.auth_store?.userInfo)) return;
  const count = Number(payload?.pending_count);
  if (Number.isFinite(count)) {
    commit("setState", { key: "approvalsPendingCount", value: count });
  } else {
    dispatch("refreshApprovalsCount");
  }
  const solicitud = traducirSolicitud(payload?.approval);
  if (solicitud && !solicitud.moneda) {
    solicitud.moneda = rootState.auth_store?.companyInfo?.currency || null;
  }
  const action = payload?.action || null;
  commit("setState", {
    key: "approvalQueueChange",
    value: { seq: state.approvalQueueChange.seq + 1, action, solicitud },
  });
  if (action === "created" && solicitud) {
    commit("pushAssignmentToast", avisoNuevaSolicitud(solicitud));
  }
};

/**
 * Abre el detalle de una gestión en el panel lateral común (FxCrmLayout), sin
 * salir de la pantalla en la que se esté. Lo usan los avisos emergentes que no
 * tienen una página propia a la que ir.
 */
/**
 * Abre el detalle en el panel lateral, desde cualquier pantalla.
 *
 * Acepta el id suelto —como la llamaban los avisos emergentes— o
 * `{ id, tab }`, donde `tab` es la pestaña del detalle en la que aterrizar
 * (`llamadas`, `agenda`, `correos`…). La campana la usa para dejar al usuario
 * justo donde se resuelve el aviso: una llamada sin tipificar en Llamadas, una
 * tarea en Agenda.
 */
export const openInteractionDrawer = ({ commit, state }, payload) => {
  const { id, tab = null } = typeof payload === "object" && payload !== null ? payload : { id: payload };
  if (id == null) return;
  commit("setState", {
    key: "detailDrawerRequest",
    value: { seq: state.detailDrawerRequest.seq + 1, id, tab },
  });
};

export const refreshApprovalsCount = async ({ commit, rootState }) => {
  if (!reassignScope(rootState.auth_store?.userInfo)) {
    commit("setState", { key: "approvalsPendingCount", value: null });
    return null;
  }
  try {
    const count = await fetchPendingCount();
    commit("setState", { key: "approvalsPendingCount", value: count });
    return count;
  } catch (error) {
    console.warn("[aprobaciones] no se pudo cargar el contador de pendientes:", error);
    return null;
  }
};

// Interpreta el `content` crudo de un mensaje/chunk de FEXIA CRM:
// - texto plano                 → mensaje del usuario.
// - JSON { response: "..." }    → respuesta del assistant.
// - JSON { detail: ... } / otro → respuesta del assistant con error
//   (p. ej. "Model response timeout") → se muestra un mensaje amigable, NO
//   el JSON crudo ni como si fuera del usuario.
const FEXIA_ERROR_TEXT =
  "⚠️ No se pudo generar la respuesta. Por favor, intentá de nuevo.";
const parseCrmMessageContent = (raw) => {
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    if (typeof parsed.response === "string") {
      return { role: "assistant", content: parsed.response, isError: false };
    }
    return { role: "assistant", content: FEXIA_ERROR_TEXT, isError: true };
  }
  return { role: "user", content: raw, isError: false };
};

// Sin `console.log` de la respuesta: por aquí pasa TODO GET del módulo, incluido
// el del tablero, que en producción trae ~950 interacciones. Volcarlo con
// DevTools abierto es lento y además la consola retiene la referencia, así que el
// GC no puede liberar las respuestas viejas.
export const getEnty = async ({ commit }, { enty, urlPath, mutation, signal }) => {
  const url = `/api/${urlPath}`;
  const res = await apiClient.get(url, signal ? { signal } : {});
  commit(mutation, { key: enty, value: res });
  return res;
};
export const saveEnty = async (
  { commit },
  { data, urlPath, mutation1, enty }
) => {
  console.log("DATA POST: ", data);
  const url = `/api/${urlPath}`;
  const res = await apiClient.post(url, data);
  console.log(`RESPONSE POST ENTY TO ${url}`, res);
  commit(mutation1, { key: enty, value: res.data });
  return res;
};

export const updateEnty = async (
  { commit },
  { key, keySearch, value, urlpath, mutation1, data, urlExtra = null }
) => {
  const url = `/api/${urlpath}${value}/${urlExtra ? urlExtra : ""}`;
  const res = await apiClient.patch(url, data);
  console.log(`RESPONSE POST ENTY TO ${url}`, res, 'Data: ',data);
  commit(mutation1, {
    key,
    keySearch,
    value,
    data: res.data,
  });
  return res;
};
export const updateEntyWithPut = async (
  { commit },
  { key, keySearch, value, urlpath, mutation1, data }
) => {
  const url = `/api/${urlpath}`;
  const res = await apiClient.put(url, data);
  console.log(`RESPONSE POST ENTY TO ${url}`, res);
  commit(mutation1, {
    key,
    keySearch,
    value,
    data: res.data,
  });
  return res;
};
export const deleteEnty = async (
  { commit },
  { key, keySearch, value, urlpath, mutation1 }
) => {
  const url = `/api/${urlpath}${value}/`;
  const res = await apiClient.delete(url);
  console.log(`RESPONSE POST ENTY TO ${url}`, res);
  commit(mutation1, {
    key,
    keySearch,
    value,
  });
  return res;
};
export const deleteEntyWithPayload = async (
  { commit },
  { data, id, keySearch, urlPath, mutation1, enty }
) => {
  console.log("DATA DELETE: ", data);
  const url = `/api/${urlPath}`;
  const res = await apiClient.delete(url, { data });
  console.log(`RESPONSE POST ENTY TO ${url}`, res);
  commit(mutation1, { key: enty, keySearch, value: id });
  return res;
};

export const basicPatch = async (context, { urlpath, value, data }) => {
  const url = `/api/${urlpath}${value}/`;
  const res = await apiClient.patch(url, data);
  console.log(`RESPONSE POST ENTY TO ${url}`, res);
  return res;
};

export const uploadFile = async ({ commit }, params) => {
  var data = new FormData();
  data.append("file", params.file);
  data.append("itx_management", params.itx_management);
  if (params.crm_data?.crm_status?.id) {
    data.append("crm_status", params.crm_data?.crm_status?.id);
  }
  if (params.crm_data?.crm_phase?.id) {
    data.append("crm_phase", params.crm_data?.crm_phase?.id);
  }
  if (params.crm_data?.crm_stage?.id) {
    data.append("crm_stage", params.crm_data?.crm_stage?.id);
  }
  if (params.comment_text) {
    data.append("comment_text", params.comment_text);
  }

  console.log("NUEVO ARCHIVO : ", params.file);
  const url = `/api/fxCrm/itxAttachment/`;
  const res = await apiClient.post(url, data);
  console.log("dataaaa -----> :", res);

  commit("addFile", { data: res.data });
};
export const deleteFile = async ({ commit }, { id }) => {
  const url = `/api/fxCrm/itxAttachment/${id}/`;
  const res = await apiClient.delete(url);
  console.log(`RESPONSE POST ENTY TO ${url}`, res);
  commit("deleteFile", { id });
  return res;
};

export const setReportForm = ({ commit }, isReportForm) => {
  commit("SET_IS_REPORT_FORM", isReportForm);
};
export const getSenders = async ({ commit }, url) => {
  const res = await apiClientJoin.get(url);
  commit("setState", { key: "senders", value: res.data.results });
};
/**
 * Comando de socket `crm_notification` (`store/modules/realtime.js`).
 *
 * Antes insertaba la notificación en la lista plana de la campana. Esa lista se
 * retiró con la página (FC-743), así que ya no hay dónde insertarla: ahora se
 * usa como DISPARADOR de recarga, que es lo único que aporta.
 *
 * Los `CrmBell/*` no emiten eventos propios —backend recalcula en cada
 * consulta—, así que mientras este comando siga llegando nos ahorra sondear
 * tan seguido. Si backend deja de emitirlo, esto simplemente no se ejecuta y no
 * rompe nada. Está preguntado en `.claude/crm_notificaciones/mensaje-backend.md`.
 */
export const appendNotificationAction = ({ dispatch }) => {
  dispatch("refreshBellCount");
};

// --- FEXIA CRM chat (integración real) ---

// Obtiene (o crea) el chat de FEXIA del usuario.
export const fetchCrmChatId = async ({ commit }) => {
  const res = await apiClient.get("/api/fexia/crm_chat_id/");
  commit("SET_CRM_CHAT_ID", res.data.chat_id);
  return res.data.chat_id;
};

// Carga el historial de mensajes del chat. El backend NO trae rol: los mensajes
// del assistant vienen como JSON {"response":"..."} (igual que los chunks del
// socket); los del usuario son texto plano → se infiere el rol por ahí.
export const fetchCrmMessages = async ({ state, commit }, chatId) => {
  const id = chatId || state.crmChat.chatId;
  if (!id) return;
  try {
    const res = await apiClient.get(`/api/fexia/crm_message/${id}/`);
    const raw = res.data?.messages || [];
    const mapped = raw.map((m, i) => {
      const { role, content } = parseCrmMessageContent(m.content);
      return {
        id: `hist-${i}-${m.timestamp}`,
        role,
        content,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        format: "text",
        file: null,
      };
    });
    commit("SET_CRM_MESSAGES", mapped);
  } catch (error) {
    // 404 → todavía no hay chat/historial: dejamos la lista vacía.
    if (error?.response?.status === 404) {
      commit("SET_CRM_MESSAGES", []);
      return;
    }
    console.error("[FEXIA CRM] fetchCrmMessages error:", error);
  }
};

// Envía el mensaje del usuario. La respuesta NO viene en este POST
// ({ status: 'processing' }) — llega en streaming por el socket.
export const sendCrmMessage = async ({ state, commit }, content) => {
  const text = (content || "").trim();
  if (!text) return;

  commit("ADD_CRM_MESSAGE", {
    id: `u-${Date.now()}`,
    role: "user",
    content: text,
    timestamp: new Date(),
  });
  commit("SET_CRM_STREAMING", true);

  try {
    let chatId = state.crmChat.chatId;
    if (!chatId) {
      const res = await apiClient.get("/api/fexia/crm_chat_id/");
      chatId = res.data.chat_id;
      commit("SET_CRM_CHAT_ID", chatId);
    }
    await apiClient.post(`/api/fexia/crm_message/${chatId}/`, { content: text });
  } catch (error) {
    console.error("[FEXIA CRM] sendCrmMessage error:", error);
    commit("SET_CRM_STREAMING", false);
  }
};

// La invoca el socket de realtime en cada chunk `new_crm_fexia_message`.
export const receiveCrmFexiaChunk = ({ state, commit }, datos) => {
  if (!datos) return;

  if (datos.chunk === "[DONE]") {
    commit("FINALIZE_CRM_MESSAGE", {
      temporalId: datos.temporal_id,
      messageId: datos.message_id,
    });
    commit("SET_CRM_STREAMING", false);
    // Respuesta completada con el panel cerrado → badge de no leídos.
    if (!state.crmChat.isPanelOpen) commit("INCREMENT_CRM_UNREAD");
    return;
  }

  // Los chunks de contenido llegan como JSON string: {"response":"..."}.
  // Si en cambio llega un error ({"detail":...}), se muestra el mensaje amigable.
  const { content: text, isError } = parseCrmMessageContent(datos.chunk);

  const existing = state.crmChat.messages.find(
    (m) => m.id === datos.temporal_id
  );
  if (!existing) {
    commit("ADD_CRM_MESSAGE", {
      id: datos.temporal_id,
      role: "assistant",
      content: text,
      format: "text",
      file: null,
      streaming: !isError,
      timestamp: new Date(),
    });
  } else {
    commit("APPEND_CRM_CHUNK", { temporalId: datos.temporal_id, text });
  }

  // Un error termina el turno: finalizar y apagar el streaming.
  if (isError) {
    commit("FINALIZE_CRM_MESSAGE", { temporalId: datos.temporal_id });
    commit("SET_CRM_STREAMING", false);
    if (!state.crmChat.isPanelOpen) commit("INCREMENT_CRM_UNREAD");
  }
};

// --- FEXIA — configuración de sugerencias (CRUD real /api/fxCrm/Suggestions/) ---

// GET: el backend devuelve un array plano; lo envolvemos en {data:{results}}
// para reusar las mutations genéricas addEnty/updateState/removeState.
export const fetchCrmSuggestions = async ({ commit }) => {
  const res = await apiClient.get("/api/fxCrm/Suggestions/");
  // Se espera un array plano; normalizamos por si llega como string
  // (content-type) o envuelto ({ results: [...] } / { data: [...] }).
  let list = res.data;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = [];
    }
  }
  if (!Array.isArray(list)) {
    list = list?.results || list?.data || [];
  }
  if (!Array.isArray(list)) list = [];
  commit("setState", {
    key: "crmSuggestions",
    value: { data: { results: list } },
  });
  return list;
};

// POST: activa se manda siempre en true.
export const createCrmSuggestion = async ({ commit }, { nombre, rol }) => {
  const res = await apiClient.post("/api/fxCrm/Suggestions/", {
    nombre,
    rol,
    activa: true,
  });
  commit("addEnty", { key: "crmSuggestions", value: res.data });
  return res.data;
};

// PATCH: solo se edita el texto (nombre).
export const updateCrmSuggestion = async ({ commit }, { id, nombre }) => {
  const res = await apiClient.patch(`/api/fxCrm/Suggestions/${id}/`, { nombre });
  commit("updateState", {
    key: "crmSuggestions",
    keySearch: "id",
    value: id,
    data: res.data,
  });
  return res.data;
};

export const deleteCrmSuggestion = async ({ commit }, id) => {
  await apiClient.delete(`/api/fxCrm/Suggestions/${id}/`);
  commit("removeState", { key: "crmSuggestions", keySearch: "id", value: id });
};

// Contador de acciones de la etapa actual de una gestión.
//
// Petición ligera a propósito: tras ejecutar una acción solo cambia este
// contador, y recargar el detalle entero (transiciones, agenda, adjuntos,
// llamadas...) para refrescar un "2 de 3" no tiene sentido.
export const fetchStageActions = async ({ commit }, itxId) => {
  if (!itxId) return null;
  const { data } = await apiClient.get(
    `/api/fxCrm/CrmItxAction/?itx_management=${itxId}`
  );
  commit("setState", {
    key: "stageActions",
    value: {
      itxId: Number(itxId),
      crm_stage: data?.crm_stage || null,
      actions: data?.actions || [],
    },
  });
  return data;
};

// Llamadas de la gestión. Va aparte del detalle porque cruzarlas con Redis/CDR
// es lo más lento de ese endpoint.
export const fetchInteractionCalls = async ({ commit }, itxId) => {
  if (!itxId) return [];
  const current = Number(itxId);
  commit("setState", {
    key: "interactionCalls",
    value: { itxId: current, loaded: false, loading: true, results: [] },
  });
  try {
    const { data } = await apiClient.get(
      `/api/fxCrm/CrmCallLog/?itx_management=${current}&limit=500`
    );
    const results = data?.results ?? data ?? [];
    commit("setState", {
      key: "interactionCalls",
      value: { itxId: current, loaded: true, loading: false, results },
    });
    return results;
  } catch (error) {
    commit("setState", {
      key: "interactionCalls",
      value: { itxId: current, loaded: false, loading: false, results: [] },
    });
    throw error;
  }
};

// --- Alertas de asignación (`.claude/alertas_crm/`) ---
// Todo pasa por `helpers/assignmentAlertsApi.js`, el único que conoce el backend.

/** Carga inicial. La llama FxCrmLayout una vez por usuario. */
export const loadAssignmentAlerts = async ({ commit, dispatch, rootState }) => {
  commit("setAssignmentAlertsLoading", true);
  try {
    const datos = await fetchAlerts(rootState.auth_store.userInfo);
    commit("setAssignmentAlerts", datos);
    dispatch("announcePendingAssignments");
  } catch (error) {
    console.error("[ALERTAS] no se pudieron cargar las alertas de asignación", error);
  } finally {
    commit("setAssignmentAlertsLoading", false);
  }
};

/**
 * Recarga silenciosa (sin aviso de entrada). El socket SOLO avisa cuando se
 * crea una alerta: «Vista» y «Resuelta» (también las del remitente, en
 * «Asignaciones enviadas») se ven en la siguiente consulta. Por eso se llama
 * al abrir la campana y después de asignar.
 */
let recargando = null;
export const refreshAssignmentAlerts = ({ commit, rootState }) => {
  if (recargando) return recargando;
  recargando = fetchAlerts(rootState.auth_store.userInfo)
    .then((datos) => commit("setAssignmentAlerts", datos))
    .catch((error) => console.error("[ALERTAS] no se pudieron recargar las alertas", error))
    .finally(() => {
      recargando = null;
    });
  return recargando;
};

/** Quién mira, para los textos de los avisos («Sebastián, tienes…»). */
const lector = (rootState) => {
  const { yo, rol } = quienMira(rootState.auth_store.userInfo);
  return { nombre: yo.nombre, rol };
};

/**
 * Aviso al ENTRAR con asignaciones sin ver (task_4 CA-02): un resumen, una
 * vez por sesión del navegador (`sessionStorage`), así un F5 no lo repite.
 * Lo que llega después, en vivo, avisa cada vez (`receiveAssignmentAlert`).
 */
export const announcePendingAssignments = ({ commit, state, rootState }) => {
  const clave = `fx_alertas_aviso_entrada:${rootState.auth_store.userInfo?.User_id ?? ""}`;
  try {
    if (sessionStorage.getItem(clave)) return;
  } catch (error) {
    // Sin sessionStorage se avisa igual (como mucho, se repite en cada F5).
  }
  const aviso = avisoEntrada(state.assignmentAlerts.recibidas, lector(rootState));
  if (!aviso) return;
  commit("pushAssignmentToast", aviso);
  try {
    sessionStorage.setItem(clave, "1");
  } catch (error) {
    // Ídem.
  }
};

/**
 * El REMITENTE confirmó una transferencia o asignación (task_4 CA-01): aviso
 * «Alerta enviada a X». Lo llama la Bandeja cuando `assign/` responde bien.
 * «Asignaciones enviadas» se pone al día: con la API, recargando `box=sent`
 * (la alerta la crea el servidor); en el mock, armándola aquí.
 *
 * @param {{ tipo: "transfer"|"assign", para?: {id, nombre}, grupo?: string,
 *           items: Array<{id, contacto, empresa}>, reparto?: string }} envio
 *   `reparto` (el título del modo) cuando reparte el servidor y no se sabe a
 *   quién le tocó cada una: aviso genérico.
 */
export const notifyAssignmentSent = ({ commit, dispatch }, envio) => {
  commit(
    "pushAssignmentToast",
    avisoEnvio({
      tipo: envio.tipo,
      para: envio.para?.nombre,
      n: envio.items.length,
      reparto: envio.reparto,
      porPersona: envio.porPersona,
    })
  );
  // La alerta enviada la crea el servidor: se recarga `box=sent`.
  dispatch("refreshAssignmentAlerts");
};

export const dismissAssignmentToast = ({ commit }, id) => {
  commit("removeAssignmentToast", id);
};

/**
 * La ÚNICA entrada de una alerta que llega en vivo: el socket y el simulador
 * de desarrollo pasan por aquí. Actualiza la campana, avisa a las vistas
 * abiertas (`assignmentArrival`) y saca el aviso emergente.
 */
export const receiveAssignmentAlert = ({ commit, state, rootState }, alerta) => {
  if (!alerta?.id) return;
  const yaEstaba = state.assignmentAlerts.recibidas.some((otra) => otra.id === alerta.id);
  commit("prependAssignmentAlert", alerta);
  // Con contador de servidor, se adelanta lo que suma; el socket lo corrige
  // enseguida con `unseen_items` (`receiveAssignmentAlertSocket`).
  if (!yaEstaba) {
    commit(
      "adjustAssignmentUnseenCount",
      alerta.items.filter((item) => item.estado === "nueva").length
    );
  }
  commit("assignmentArrived", {
    ids: alerta.items.map((item) => item.id),
    tipo: alerta.tipo,
    alertaId: alerta.id,
  });
  commit("pushAssignmentToast", avisoLlegada(alerta, lector(rootState)));
};

/**
 * Evento `crm_assignment_alert` del socket de notificaciones (realtime.js):
 * `datos = { alert, unseen_items }`.
 */
export const receiveAssignmentAlertSocket = ({ commit, dispatch }, datos) => {
  const alerta = traducirAlertaSocket(datos);
  dispatch("receiveAssignmentAlert", alerta);
  if (Number.isInteger(datos?.unseen_items)) {
    commit("setAssignmentUnseenCount", datos.unseen_items);
  }
};

/**
 * Marca vistas con actualización optimista: se pinta enseguida (estado y
 * contador) y, si el servicio falla, se vuelve atrás. `elegir(item)` decide qué
 * items «nueva» del store entran; `enviar(itemIds)` hace la llamada y devuelve
 * `{ contador, managements }` (o `null` en el mock).
 *
 * `siempre`: llama aunque en el store no haya nada que marcar. Lo usa «Marcar
 * todo», porque puede haber avisos que solo vienen en las filas (alertas que
 * la campana no cargó). Las tarjetas que el backend dice que perdieron el
 * aviso (`managements`) se apuntan como vistas, para apagar también esas
 * franjas sin recargar.
 */
const marcarVistas = async ({ commit, state }, elegir, enviar, { siempre = false } = {}) => {
  const afectados = state.assignmentAlerts.recibidas.flatMap((alerta) =>
    alerta.items.filter((item) => item.estado === "nueva" && elegir(item))
  );
  if (!afectados.length && !siempre) return true;
  const cambios = afectados.map((item) => ({ alertaId: item.alertaId, id: item.id }));
  const itemIds = afectados.map((item) => item.itemId);
  commit("setAssignmentItemsEstado", { cambios, estado: "vista" });
  commit("adjustAssignmentUnseenCount", -afectados.length);
  try {
    const respuesta = await enviar(itemIds);
    if (Number.isInteger(respuesta?.contador)) {
      commit("setAssignmentUnseenCount", respuesta.contador);
    }
    if (respuesta?.managements?.length) {
      commit("markAssignmentRowsSeen", respuesta.managements);
    }
    return true;
  } catch (error) {
    console.error("[ALERTAS] no se pudo marcar como visto", error);
    commit("setAssignmentItemsEstado", { cambios, estado: "nueva" });
    commit("adjustAssignmentUnseenCount", afectados.length);
    return false;
  }
};

/**
 * Marca vista la alerta de UNA tarjeta o fila, venga del store o solo de la
 * propia fila (`assignment_alert`). Es lo que usan «Enterado», «Marcar como
 * vista» y abrir la tarjeta.
 *
 * @param {{ id, itemId }} item  el que devuelve el getter `assignmentAlertFor`.
 */
export const markAssignmentItemSeen = async (ctx, item) => {
  if (!item) return true;
  const { commit, state, rootState } = ctx;
  const enStore = state.assignmentAlerts.recibidas.some((alerta) =>
    alerta.items.some((otro) => otro.estado === "nueva" && String(otro.id) === String(item.id))
  );
  if (enStore) return markAssignmentItemsSeen(ctx, [item.id]);

  // Solo en la fila: se apaga ya y se avisa al backend con su `item_id`.
  commit("markAssignmentRowsSeen", [item.id]);
  commit("adjustAssignmentUnseenCount", -1);
  try {
    const respuesta = await markItemsSeen(rootState.auth_store.userInfo, [item.itemId]);
    if (Number.isInteger(respuesta?.contador)) {
      commit("setAssignmentUnseenCount", respuesta.contador);
    }
    return true;
  } catch (error) {
    console.error("[ALERTAS] no se pudo marcar como visto", error);
    commit("unmarkAssignmentRowSeen", item.id);
    commit("adjustAssignmentUnseenCount", 1);
    return false;
  }
};

/**
 * «Enterado», «Marcar como vista», abrir la tarjeta: estas filas o tarjetas
 * (ids de VISTA: interacción en la Bandeja, gestión en las tarjetas), en todas
 * sus alertas. Al backend van los `itemId`.
 */
export const markAssignmentItemsSeen = (ctx, ids) => {
  const marcar = new Set((ids || []).map(String));
  const userInfo = ctx.rootState.auth_store.userInfo;
  return marcarVistas(
    ctx,
    (item) => marcar.has(String(item.id)),
    (itemIds) => markItemsSeen(userInfo, itemIds)
  );
};

/** Una alerta entera. */
export const markAssignmentAlertSeen = (ctx, alertaId) =>
  marcarVistas(
    ctx,
    (item) => item.alertaId === alertaId,
    () => markAlertSeen(ctx.rootState.auth_store.userInfo, alertaId)
  );

/** «Marcar todo como visto»: llama siempre (puede haber avisos solo en filas). */
export const markAllAssignmentsSeen = (ctx) =>
  marcarVistas(
    ctx,
    () => true,
    () => markAllSeen(ctx.rootState.auth_store.userInfo),
    { siempre: true }
  );

/**
 * Estas filas salieron de la vista de quien las recibió (las repartió o se
 * borraron): su alerta queda «resuelta». Lo llama la Bandeja al asignar o
 * eliminar con éxito. En la API lo resuelve el servidor (`resolution`); esto
 * solo adelanta lo que se ve hasta la próxima consulta.
 *
 * @param {{ ids: Array, motivo?: "repartida"|"reasignada"|"retirada" }} payload
 */
export const resolveAssignmentItems = ({ commit, state }, { ids, motivo = "repartida" }) => {
  const marcar = new Set((ids || []).map(String));
  const afectados = state.assignmentAlerts.recibidas.flatMap((alerta) =>
    alerta.items.filter((item) => item.estado !== "resuelta" && marcar.has(String(item.id)))
  );
  if (!afectados.length) return;
  const sinVer = afectados.filter((item) => item.estado === "nueva").length;
  commit("setAssignmentItemsEstado", {
    cambios: afectados.map((item) => ({ alertaId: item.alertaId, id: item.id })),
    estado: "resuelta",
    motivo,
  });
  commit("adjustAssignmentUnseenCount", -sinVer);
};

/** SOLO DESARROLLO: simula la llegada en vivo de una alerta (en memoria). */
export const simulateAssignmentAlert = ({ dispatch, rootState }, opciones) => {
  const alerta = simularAlerta(rootState.auth_store.userInfo, opciones);
  dispatch("receiveAssignmentAlert", alerta);
  return alerta;
};

// ---------------------------------------------------------------------------
// Campana del CRM (FC-743) — `.claude/crm_notificaciones/`
// ---------------------------------------------------------------------------

const GRUPOS_CAMPANA = {
  toDo: fetchToDo,
  news: fetchNews,
  followUp: fetchFollowUp,
};

/**
 * Trae un grupo de la campana. `grupo` es toDo | news | followUp.
 *
 * Si falla, deja el grupo vacío en vez de reventar: la campana es accesorio de
 * la pantalla, no puede tumbar el CRM. El usuario sin rol o sin empresa recibe
 * un 200 con listas vacías, así que ese caso ya viene resuelto de backend.
 */
export const loadBellGroup = async ({ commit, rootState }, grupo) => {
  const traer = GRUPOS_CAMPANA[grupo];
  if (!traer) return;
  // Sin sesión no se pide nada, y la guarda va AQUÍ para que cubra a todos los
  // que llaman: la apertura del panel, el cambio de ruta, la vuelta a la
  // pestaña y los refrescos tras guardar.
  //
  // No es cosmético: el interceptor de `apiClient` arma el baseURL con
  // `https://${userInfo.PbxServer}`, así que sin sesión la petición sale a
  // `https://undefined/...` — otro origen, que el navegador rechaza y REPORTA
  // COMO ERROR DE CORS. Es el mismo guardado (`PbxServer && Token`) que usa el
  // resto del layout.
  const sesion = rootState.auth_store?.userInfo;
  if (!sesion?.PbxServer || !sesion?.Token) return;
  commit("setBellLoading", { grupo, valor: true });
  try {
    commit("setBellGroup", { grupo, valor: await traer() });
  } catch (error) {
    console.error(`Error cargando la campana (${grupo}):`, error);
    commit("setBellGroup", {
      grupo,
      valor: grupo === "followUp" ? seguimientoVacio() : grupoVacio(),
    });
  } finally {
    commit("setBellLoading", { grupo, valor: false });
  }
};

/**
 * Lo que se pide al abrir el panel: los tres grupos a la vez.
 *
 * En paralelo y con `allSettled` porque son independientes: que Novedades falle
 * no puede dejar las otras dos pestañas en blanco.
 */
export const loadBellAll = ({ dispatch }) =>
  Promise.allSettled(
    Object.keys(GRUPOS_CAMPANA).map((grupo) => dispatch("loadBellGroup", grupo))
  );

/**
 * Solo el contador de la campana. Es lo que se sondea con el panel cerrado:
 * `to_do/` es el único grupo que alimenta el badge.
 *
 * TODO: si backend nos da el endpoint ligero que le pedimos (pregunta 5 de
 * `mensaje-backend.md`), cambiar esto por él — hoy nos traemos todas las
 * tarjetas para pintar un número.
 */
export const refreshBellCount = ({ dispatch }) => dispatch("loadBellGroup", "toDo");

/**
 * «✓ Cumplir» una tarea: marca el evento de agenda como realizado y retira el
 * aviso de los tres grupos sin cerrar el panel.
 *
 * El 404 no se trata como error: significa que el aviso ya no aplicaba, así que
 * se quita igual y se recarga para re-sincronizar.
 *
 * @param {number|string} agendaId - `data.agenda_id` del aviso.
 * @returns {Promise<boolean>} si la tarea quedó cumplida de verdad.
 */
export const completeBellTask = async ({ commit, dispatch }, agendaId) => {
  try {
    await completeTask(agendaId);
    commit("removeBellAgendaItem", agendaId);
    return true;
  } catch (error) {
    if (esTareaDesaparecida(error)) {
      commit("removeBellAgendaItem", agendaId);
      dispatch("loadBellAll");
      return false;
    }
    console.error("Error cumpliendo la tarea:", error);
    throw error;
  }
};
